import { joinURL } from "ufo";
import { shouldNoIndexPath } from "#shared/noIndexPaths";
import {
  resolveMetaDescription,
  resolvePageHeading,
} from "@/utils/pageHeading";
import { slimPageStatePayload } from "@/utils/slimPayload";

/**
 * Returns the currently active page, similar to Kirby's `$page` global variable
 */
export function usePage<T extends Record<string, any> = Record<string, any>>() {
  return useState<T>("app.page", () => ({}) as T);
}

/**
 * Sets the currently active page and updates the document head
 */
export function setPage<T extends Record<string, any>>(page?: T) {
  const pageState = usePageState();

  if (!page) {
    pageState.value = "rejected";
    return;
  }

  const slim = slimPageStatePayload(page) as T;
  usePage().value = slim;

  // Build the page meta tags
  const { siteUrl } = useRuntimeConfig().public;
  const site = useSite();
  const title = slim?.meta?.metaTitle
    ? `${slim?.meta?.metaTitle}`
    : resolvePageHeading(slim, site.value);
  const description = resolveMetaDescription(slim, site.value);
  const url = joinURL(siteUrl, useRoute().path);
  const ogTitle = slim?.meta?.metaTitle || title;
  const ogDescription = slim?.meta?.ogDescription || description;
  const ogImage = slim?.meta?.ogImage;

  useHead({
    bodyAttrs: {
      "data-template": slim.intendedTemplate || "default",
    },
  });

  useServerHead({
    link: [{ rel: "canonical", href: url }],
  });

  useSeoMeta({
    title,
  });

  const noIndex = shouldNoIndexPath(useRoute().path);

  useServerSeoMeta({
    description,
    ogTitle,
    ogDescription,
    ogUrl: url,
    ogType: "website",
    ogImage,
    twitterTitle: title,
    twitterDescription: description,
    twitterCard: "summary",
    ...(noIndex ? { robots: "noindex, nofollow" } : {}),
  });

  // Reviews CMS copy still says "5 years" in places — replace for HTML meta too.
  if (
    useRoute().path.replace(/\/+$/, "") === "/reviews" &&
    /\b5 years\b/i.test(description)
  ) {
    const reviewsDescription =
      "Independent Feefo customer reviews for Mont Blanc Treks guided and self-guided trekking holidays.";
    useServerSeoMeta({
      description: reviewsDescription,
      ogDescription: reviewsDescription,
      twitterDescription: reviewsDescription,
    });
  }

  pageState.value = "resolved";
}

/**
 * Returns a promise that resolves when the page data has been loaded or rejected
 */
export async function hasPage() {
  const state = usePageState();

  await until(state).not.toBe("pending");

  return state.value === "resolved";
}

function usePageState() {
  return useState<"pending" | "resolved" | "rejected">(
    "app.state.page",
    () => "pending",
  );
}
